/**
 * LLM-9F — fail-closed runtime guard for coaching memory management GET responses.
 */

const MAX_LABEL_LENGTH = 120;
const MAX_VALUE_LENGTH = 500;
const MAX_COMMAND_LENGTH = 500;
const MAX_PRESENTATION_ID_LENGTH = 64;
const VALID_SCOPES = new Set(["GLOBAL", "SPORT"]);

const isBoundedString = (value, maxLength) =>
  typeof value === "string"
  && value.trim().length > 0
  && value.trim().length <= maxLength;

const stripAuthorityFields = (item) => {
  if (item == null || typeof item !== "object" || Array.isArray(item)) {
    return null;
  }
  const {
    presentation_id: presentationId,
    label,
    value,
    scope,
    sport,
    updated_at: updatedAt,
    forget_command: forgetCommand,
  } = item;

  if (!isBoundedString(presentationId, MAX_PRESENTATION_ID_LENGTH)) {
    return null;
  }
  if (!isBoundedString(label, MAX_LABEL_LENGTH)) {
    return null;
  }
  if (!isBoundedString(value, MAX_VALUE_LENGTH)) {
    return null;
  }
  if (!VALID_SCOPES.has(scope)) {
    return null;
  }
  if (scope === "SPORT" && !isBoundedString(sport, 64)) {
    return null;
  }
  if (scope === "GLOBAL" && sport != null && sport !== "") {
    return null;
  }
  if (!updatedAt) {
    return null;
  }
  if (!isBoundedString(forgetCommand, MAX_COMMAND_LENGTH)) {
    return null;
  }

  return {
    presentationId,
    label,
    value,
    scope,
    sport: scope === "SPORT" ? sport : null,
    updatedAt,
    forgetCommand,
  };
};

/**
 * @param {unknown} raw
 * @returns {{ version: string, total: number, items: ReturnType<typeof stripAuthorityFields>[], forgetAllCommand: string | null }}
 */
export const normalizeCoachingMemoryManagementResponse = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return { version: null, total: 0, items: [], forgetAllCommand: null, malformed: true };
  }

  const version = typeof raw.version === "string" ? raw.version : null;
  const total = Number.isInteger(raw.total) && raw.total >= 0 ? raw.total : null;
  if (!Array.isArray(raw.items) || total == null) {
    return { version, total: 0, items: [], forgetAllCommand: null, malformed: true };
  }

  const items = [];
  for (const item of raw.items) {
    const normalized = stripAuthorityFields(item);
    if (normalized) {
      items.push(normalized);
    }
  }

  let forgetAllCommand = null;
  if (raw.forget_all_command != null) {
    if (!isBoundedString(raw.forget_all_command, MAX_COMMAND_LENGTH)) {
      return { version, total: 0, items: [], forgetAllCommand: null, malformed: true };
    }
    forgetAllCommand = raw.forget_all_command.trim();
  }

  if (items.length === 0) {
    forgetAllCommand = null;
  }

  return {
    version,
    total: items.length,
    items,
    forgetAllCommand,
    malformed: false,
  };
};

/** Ensure malicious API fields are never forwarded into session messages. */
export const buildForgetSessionPayload = (command) => ({
  message: command,
  payload: {},
  field_values: {},
});
